import { inject, Injectable } from '@angular/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { isConnectedGuideline } from 'shared/util-common';

import { GuidelineBase, Guideline } from '@treetop/shared/model';

import { UserService } from '../application/user.service';

@Injectable({ providedIn: 'root' })
export class PermissionResolverService {
  private userService = inject(UserService);
  private permissionsService = inject(NgxPermissionsService);

  public isAllowedToEdit(guideline: Guideline): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    if (!guideline.uniqueId) {
      return currentUserPermissions.addGuideline != null;
    }

    if (isConnectedGuideline(guideline.guidelineOrigin)) {
      return false;
    }

    if (guideline.organisationId !== this.userService.user().currentOrganisation?.id) {
      return false;
    }

    const isGuidelineOwner: boolean = guideline.ownerId === this.userService.user().id;
    const isAuthor: boolean = guideline.authors?.some((user) => user.id === this.userService.user().id);
    const allEditorsCanEdit: boolean = guideline.allEditorsCanEdit;

    return allEditorsCanEdit || isGuidelineOwner || isAuthor || currentUserPermissions.editAllGuidelines != null;
  }

  public isAllowedToPublish(guidelineDto: Guideline): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    const isGuidelineOwner: boolean = guidelineDto.ownerId === this.userService.user().id;
    return isGuidelineOwner || currentUserPermissions.publishGuideline != null;
  }

  public isAllowedToDeactivate(guidelineDto: Guideline): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    const isGuidelineOwner: boolean = guidelineDto.ownerId === this.userService.user().id;
    const isAuthor: boolean = guidelineDto.authors.some((user) => user.id === this.userService.user().id);
    const allEditorsCanEdit: boolean = guidelineDto.allEditorsCanEdit;

    return guidelineDto.environment?.hasPublishedVersion
      ? isGuidelineOwner || currentUserPermissions.deactivateGuideline != null
      : isGuidelineOwner || currentUserPermissions.deactivateGuideline != null || isAuthor || allEditorsCanEdit;
  }

  public isAllowedToApprove(guidelineDto: Guideline): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    const isGuidelineOwner: boolean = guidelineDto.ownerId === this.userService.user().id;
    const isAuthor: boolean = guidelineDto.authors.some((user) => user.id === this.userService.user().id);
    const allEditorsCanEdit: boolean = guidelineDto.allEditorsCanEdit;

    return isGuidelineOwner || isAuthor || currentUserPermissions.approveGuideline != null || allEditorsCanEdit;
  }

  public isAllowedToReview(guidelineDto: Guideline): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    const isGuidelineOwner: boolean = guidelineDto.ownerId === this.userService.user().id;
    return isGuidelineOwner || currentUserPermissions.setInReviewGuideline != null;
  }

  public isAllowedToApproveWithoutConfirmation(guidelineDto: Guideline): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    const isGuidelineOwner: boolean = guidelineDto.ownerId === this.userService.user().id;

    return isGuidelineOwner || currentUserPermissions.approveGuideline != null;
  }

  public isAllowedToDelete(): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    // According to https://treetop.atlassian.net/wiki/spaces/TREETOP/pages/186581050/Roles+Permissions
    // guideline owner is not allowed to delete
    return currentUserPermissions.removeGuideline != null;
  }

  public isAllowedToEditTaxonomy(guidelineData?: GuidelineBase): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    const isGuidelineOwner = guidelineData?.ownerId === this.userService.user().id;

    return isGuidelineOwner || currentUserPermissions.editTaxonomy != null;
  }

  public isAllowedToSyncWithHub(): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    return currentUserPermissions.syncWithHub != null;
  }

  public isAllowedToViewHubInHeader(): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    return currentUserPermissions.viewManagePage != null;
  }

  public isAllowedToEditOrganisationSettings(): boolean {
    const currentUserPermissions = this.permissionsService.getPermissions();

    return currentUserPermissions.editOrganisationSettings != null;
  }

  public isAllowedToAddReviewLink(guideline: Guideline): boolean {
    const isGuidelineOwner: boolean = guideline.ownerId === this.userService.user().id;
    const isUserSeniorClinician: boolean = this.userService.isCurrentUserInSeniorClinicianRole();

    return isGuidelineOwner || isUserSeniorClinician;
  }

  public isAllowedToReachHubSettings(guideline: Guideline): boolean {
    const isGuidelineOwner: boolean = guideline.ownerId === this.userService.user().id;
    const isUserSeniorClinician: boolean = this.userService.isCurrentUserInSeniorClinicianRole();

    return isGuidelineOwner || isUserSeniorClinician;
  }

  public isAllowedToEditGuidelineSettings(guideline: Guideline): boolean {
    const isGuidelineOwner: boolean = guideline.ownerId === this.userService.user().id;
    const isAllowedToAddReviewLink = this.isAllowedToAddReviewLink(guideline);
    const isAllowedToReachHubSettings = this.isAllowedToReachHubSettings(guideline);
    const isAllowedToDeactivate = this.isAllowedToDeactivate(guideline);

    return isGuidelineOwner || isAllowedToAddReviewLink || isAllowedToReachHubSettings || isAllowedToDeactivate;
  }
}
